import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { BarChart3, Download } from "lucide-react";
import DataTable from "@/components/mui/DataTable";

import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  Chip,
  Stack,
} from "@mui/material";

export default function Reports() {
  const [reportType, setReportType] = useState("sales");
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reportUsesDate = !["stock", "costs"].includes(reportType);

  // Sempre que mudar o tipo de relatório, limpa os dados e mostra placeholder
  useEffect(() => {
    setReportData(null);
  }, [reportType]);

  const fetchAllPaginated = async (url, params = {}) => {
    const firstRes = await axios.get(url, {
      params: { ...params, per_page: 500, page: 1 },
    });

    const firstData = firstRes.data;
    if (!firstData || !Array.isArray(firstData.data)) {
      return firstData?.data || firstData || [];
    }

    const firstItems = firstData.data;
    const lastPage =
      Number(firstData?.last_page ?? firstData?.meta?.last_page ?? 1) || 1;

    if (lastPage <= 1) {
      return firstItems;
    }

    const requests = [];
    for (let page = 2; page <= lastPage; page++) {
      requests.push(
        axios.get(url, {
          params: { ...params, per_page: 500, page },
        })
      );
    }

    const responses = await Promise.all(requests);
    const restItems = responses.flatMap((res) =>
      Array.isArray(res.data?.data) ? res.data.data : []
    );

    return [...firstItems, ...restItems];
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      let data;

      switch (reportType) {
        /**
         * RELATÓRIO DE VENDAS
         * - Apenas vendas (is_budget = 0)
         * - Usa date_from/date_to direto no backend
         */
        case "sales": {
          const salesRes = await axios.get(`${API}/sales`, {
            params: {
              per_page: -1,
              is_budget: 0,       // somente vendas
              date_from: startDate,
              date_to: endDate,
            },
          });

          const sales = salesRes.data.data || salesRes.data;

          data = {
            title: "Relatório de Vendas",
            count: sales.length,
            total: sales.reduce(
              (sum, sale) => sum + Number(sale.total ?? 0),
              0
            ),
            items: sales,
          };
          break;
        }

        /**
         * RELATÓRIO DE ORÇAMENTOS
         * - Apenas orçamentos (is_budget = 1)
         * - Usa o mesmo período de data de vendas
         */
        case "budgets": {
          const budgetsRes = await axios.get(`${API}/sales`, {
            params: {
              per_page: -1,
              is_budget: 1,       // somente orçamentos
              date_from: startDate,
              date_to: endDate,
            },
          });

          const budgets = budgetsRes.data.data || budgetsRes.data;

          data = {
            title: "Relatório de Orçamentos",
            count: budgets.length,
            total: budgets.reduce(
              (sum, sale) => sum + Number(sale.total ?? 0),
              0
            ),
            items: budgets,
          };
          break;
        }

        case "stock": {
          const products = await fetchAllPaginated(`${API}/products`);
          const totalProducts = products.length;

          data = {
            title: "Relatório de Estoque",
            total: totalProducts,
            lowStock: products.filter(
              (p) => Number(p.stock ?? 0) <= Number(p.min_stock ?? 0)
            ).length,
            items: products,
          };
          break;
        }

        case "costs": {
          const costsRes = await axios.get(`${API}/reports/costs`);
          const costsReport = costsRes.data;
          const items = costsReport.items || [];

          data = {
            title: "Relatório de Custos e Gastos",
            count: costsReport.summary?.total_products ?? items.length,
            totalCost: costsReport.summary?.total_stock_cost ?? 0,
            totalSaleValue:
              costsReport.summary?.total_stock_sale_value ?? 0,
            items,
          };
          break;
        }

        case "finance": {
          const financeRes = await axios.get(`${API}/finance`, {
            params: { per_page: 1000 },
          });
          const financeData = financeRes.data.data || financeRes.data;

          const start = new Date(startDate);
          const end = new Date(endDate + "T23:59:59");

          const inDueRange = (acc) => {
            const due = new Date(acc.due_date);
            return due >= start && due <= end;
          };

          const inPaidRange = (acc) => {
            if (!acc.paid_date) return false;
            const paidDate = new Date(acc.paid_date);
            return paidDate >= start && paidDate <= end;
          };

          const receivablePendingAccounts = financeData.filter(
            (acc) =>
              acc.type === "receber" &&
              acc.paid === false &&
              inDueRange(acc)
          );

          const payablePendingAccounts = financeData.filter(
            (acc) =>
              acc.type === "pagar" &&
              acc.paid === false &&
              inDueRange(acc)
          );

          const receivablePaidAccounts = financeData.filter(
            (acc) =>
              acc.type === "receber" &&
              acc.paid === true &&
              inPaidRange(acc)
          );

          const payablePaidAccounts = financeData.filter(
            (acc) =>
              acc.type === "pagar" &&
              acc.paid === true &&
              inPaidRange(acc)
          );

          const toNumber = (v) => Number(v ?? 0);

          const receivable = receivablePendingAccounts.reduce(
            (sum, acc) => sum + toNumber(acc.amount),
            0
          );

          const payable = payablePendingAccounts.reduce(
            (sum, acc) => sum + toNumber(acc.amount),
            0
          );

          const received = receivablePaidAccounts.reduce(
            (sum, acc) => sum + toNumber(acc.amount),
            0
          );

          const paid = payablePaidAccounts.reduce(
            (sum, acc) => sum + toNumber(acc.amount),
            0
          );

          const accountsForTable = [
            ...receivablePendingAccounts,
            ...payablePendingAccounts,
            ...receivablePaidAccounts,
            ...payablePaidAccounts,
          ];

          const uniqueAccounts = Array.from(
            new Map(accountsForTable.map((acc) => [acc.id, acc])).values()
          );

          data = {
            title: "Relatório Financeiro",
            receivable,
            payable,
            received,
            paid,
            items: uniqueAccounts,
          };
          break;
        }

        case "cashier": {
          const cashierData = await fetchAllPaginated(`${API}/cashiers`);

          const cashiers = cashierData.filter((c) => {
            const openDate = new Date(c.opened_at);
            return (
              openDate >= new Date(startDate) &&
              openDate <= new Date(endDate + "T23:59:59")
            );
          });

          const isOpen = (status) =>
            status === "open" ||
            status === "aberto" ||
            status === "ABERTO";

          const isClosed = (status) =>
            status === "closed" ||
            status === "fechado" ||
            status === "FECHADO";

          data = {
            title: "Relatório de Caixa",
            total: cashiers.length,
            open: cashiers.filter((c) => isOpen(c.status)).length,
            closed: cashiers.filter((c) =>
              isClosed(c.status)
            ).length,
            items: cashiers,
          };
          break;
        }

        default:
          data = null;
      }

      setReportData(data);
      toast.success("Relatório gerado!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!reportData || !reportData.items) return;

    let csv = "";
    let filename = "relatorio.csv";

    const statusLabel = (status) => {
      if (status === "open") return "aberto";
      if (status === "closed") return "fechado";
      return status;
    };

    switch (reportType) {
      case "sales": {
        csv = "Data,Cliente,Total,Pagamento\n";
        reportData.items.forEach((sale) => {
          const dateStr = new Date(
            sale.created_at
          ).toLocaleString("pt-BR");

          const customerName =
            sale.customer?.name || "N/A";

          csv += `${dateStr},${customerName},${Number(
            sale.total ?? 0
          )},${sale.payment_method}\n`;
        });
        filename = "relatorio-vendas.csv";
        break;
      }

      case "budgets": {
        csv = "Data,Cliente,Total,Status\n";
        reportData.items.forEach((sale) => {
          const dateStr = new Date(
            sale.created_at
          ).toLocaleString("pt-BR");

          const customerName =
            sale.customer?.name || "N/A";

          csv += `${dateStr},${customerName},${Number(
            sale.total ?? 0
          )},${sale.status}\n`;
        });
        filename = "relatorio-orcamentos.csv";
        break;
      }

      case "stock": {
        csv = "Produto,Estoque Atual,Estoque Mínimo,Preço\n";
        reportData.items.forEach((product) => {
          csv += `${product.name},${Number(product.stock ?? 0)},${product.min_stock
            },${Number(product.sale_price ?? 0)}\n`;
        });
        filename = "relatorio-estoque.csv";
        break;
      }

      case "costs": {
        csv =
          "Produto,Código,Estoque,Custo Unitário,Preço de Venda,Valor Total de Custo,Valor Total de Venda\n";
        reportData.items.forEach((product) => {
          csv += `${product.name},${product.barcode || ""},${Number(
            product.stock ?? 0
          )},${Number(product.cost_price ?? 0)},${Number(
            product.sale_price ?? 0
          )},${Number(product.total_cost ?? 0)},${Number(
            product.total_sale_value ?? 0
          )}\n`;
        });
        filename = "relatorio-custos-e-gastos.csv";
        break;
      }

      case "finance": {
        csv = "Tipo,Descrição,Valor,Vencimento,Status\n";
        reportData.items.forEach((account) => {
          csv += `${account.type},${account.description},${Number(
            account.amount ?? 0
          )},${new Date(
            account.due_date
          ).toLocaleDateString("pt-BR")},${account.paid ? "Pago" : "Pendente"
            }\n`;
        });
        filename = "relatorio-financeiro.csv";
        break;
      }

      case "cashier": {
        csv =
          "Operador,Abertura,Fechamento,Valor Inicial,Valor Final,Status\n";
        reportData.items.forEach((cashier) => {
          csv += `${cashier.user_name},${new Date(
            cashier.opened_at
          ).toLocaleString("pt-BR")},${cashier.closed_at
            ? new Date(
              cashier.closed_at
            ).toLocaleString("pt-BR")
            : "N/A"
            },${Number(cashier.opening_amount ?? 0)},${cashier.closing_amount != null
              ? Number(cashier.closing_amount)
              : "N/A"
            },${statusLabel(cashier.status)}\n`;
        });
        filename = "relatorio-caixa.csv";
        break;
      }

      default:
        break;
    }

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    toast.success("Relatório exportado!");
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  const reportTitleMap = {
    sales: "Relatório de Vendas",
    budgets: "Relatório de Orçamentos",
    stock: "Relatório de Estoque",
    costs: "Relatório de Custos e Gastos",
    finance: "Relatório Financeiro",
    cashier: "Relatório de Caixa",
  };

  const costColumns = [
    {
      id: "name",
      label: "Produto",
      render: (product) => (
        <Typography fontWeight={600}>{product.name}</Typography>
      ),
    },
    {
      id: "barcode",
      label: "Código",
      render: (product) => product.barcode || "-",
      hideOnMobile: true,
    },
    {
      id: "stock",
      label: "Estoque",
      align: "right",
      render: (product) => Number(product.stock ?? 0),
    },
    {
      id: "cost_price",
      label: "Custo Unit.",
      align: "right",
      render: (product) => formatMoney(product.cost_price),
    },
    {
      id: "sale_price",
      label: "Preço Venda",
      align: "right",
      render: (product) => formatMoney(product.sale_price),
    },
    {
      id: "total_cost",
      label: "Total Custo",
      align: "right",
      render: (product) => formatMoney(product.total_cost),
    },
    {
      id: "total_sale_value",
      label: "Total Venda",
      align: "right",
      render: (product) => formatMoney(product.total_sale_value),
    },
  ];

  return (
    <Box data-testid="reports-page">
      {/* Card de filtros / geração */}
      <Paper
        className="data-table-container"
        sx={{ mb: 3, overflow: "hidden" }}
      >
        <Box
          className="table-header"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2,
          }}
        >
          <Typography className="table-title" variant="h6">
            Gerar Relatório
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Os relatórios agora carregam todos os registros disponíveis, sem limite fixo de paginação.
          </Alert>

          {/* Filtros */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 2.5,
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: 200 }}
            >
              <InputLabel id="report-type-label">
                Tipo de Relatório
              </InputLabel>
              <Select
                labelId="report-type-label"
                label="Tipo de Relatório"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                data-testid="report-type-select"
              >
                <MenuItem value="sales">Vendas</MenuItem>
                <MenuItem value="budgets">Orçamentos</MenuItem>
                <MenuItem value="stock">Estoque</MenuItem>
                <MenuItem value="costs">Custos e Gastos</MenuItem>
                <MenuItem value="finance">Financeiro</MenuItem>
                <MenuItem value="cashier">Caixa</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Data Inicial"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={!reportUsesDate}
              data-testid="report-start-date"
            />

            <TextField
              label="Data Final"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={!reportUsesDate}
              data-testid="report-end-date"
            />
          </Box>

          {/* Botões */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<BarChart3 size={16} />}
              onClick={generateReport}
              disabled={loading}
              data-testid="generate-report-btn"
            >
              {loading ? "Gerando..." : "Gerar Relatório"}
            </Button>

            {reportData && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Download size={16} />}
                onClick={exportToCSV}
                data-testid="export-report-btn"
              >
                Exportar CSV
              </Button>
            )}
          </Box>

          {reportData && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
              <Chip
                size="small"
                label={`Tipo: ${reportTitleMap[reportType]}`}
                color="primary"
                variant="outlined"
              />
              {reportUsesDate && (
                <Chip
                  size="small"
                  label={`Período: ${new Date(startDate).toLocaleDateString("pt-BR")} a ${new Date(endDate).toLocaleDateString("pt-BR")}`}
                  variant="outlined"
                />
              )}
              {typeof reportData.count === "number" && (
                <Chip
                  size="small"
                  label={`Registros: ${reportData.count}`}
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      {/* Resultado do relatório */}
      <Paper className="data-table-container">
        <Box
          className="table-header"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2,
          }}
        >
          <Typography className="table-title" variant="h6">
            {reportTitleMap[reportType]}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Placeholder enquanto não gerou */}
          {!reportData && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="report-placeholder"
            >
              Selecione o período e clique em "Gerar Relatório" para
              visualizar os dados.
            </Typography>
          )}

          {/* VENDAS */}
          {reportData && reportType === "sales" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total de Vendas
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-sales-count"
                  >
                    {reportData.count}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Receita Total
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-sales-total"
                  >
                    {formatMoney(reportData.total)}
                  </Typography>
                </Paper>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Período:{" "}
                {new Date(startDate).toLocaleDateString("pt-BR")} a{" "}
                {new Date(endDate).toLocaleDateString("pt-BR")}
              </Typography>
            </>
          )}

          {/* ORÇAMENTOS */}
          {reportData && reportType === "budgets" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total de Orçamentos
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-budgets-count"
                  >
                    {reportData.count}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Valor Total Orçado
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-budgets-total"
                  >
                    {formatMoney(reportData.total)}
                  </Typography>
                </Paper>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Período:{" "}
                {new Date(startDate).toLocaleDateString("pt-BR")} a{" "}
                {new Date(endDate).toLocaleDateString("pt-BR")}
              </Typography>
            </>
          )}

          {/* ESTOQUE */}
          {reportData && reportType === "stock" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total de Produtos
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-stock-total"
                  >
                    {reportData.total}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Produtos Abaixo do Mínimo
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#f59e0b",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-stock-low"
                  >
                    {reportData.lowStock}
                  </Typography>
                </Paper>
              </Box>
            </>
          )}

          {/* CUSTOS E GASTOS */}
          {reportData && reportType === "costs" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total de Produtos
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-costs-count"
                  >
                    {reportData.count}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Custo Total do Estoque
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#ef4444",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-costs-total-cost"
                  >
                    {formatMoney(reportData.totalCost)}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Valor de Venda do Estoque
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-costs-total-sale"
                  >
                    {formatMoney(reportData.totalSaleValue)}
                  </Typography>
                </Paper>
              </Box>

              <DataTable
                columns={costColumns}
                rows={reportData.items}
                getRowTestId={(row) => `report-cost-product-${row.id}`}
                emptyMessage="Nenhum produto encontrado"
                containerSx={{ p: 0 }}
              />
            </>
          )}

          {/* FINANCEIRO */}
          {reportData && reportType === "finance" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    A Receber
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-finance-receivable"
                  >
                    {formatMoney(reportData.receivable)}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    A Pagar
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#ef4444",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-finance-payable"
                  >
                    {formatMoney(reportData.payable)}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Recebido
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#0ea5e9",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-finance-received"
                  >
                    {formatMoney(reportData.received)}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Pago
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#0ea5e9",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-finance-paid"
                  >
                    {formatMoney(reportData.paid)}
                  </Typography>
                </Paper>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Período:{" "}
                {new Date(startDate).toLocaleDateString("pt-BR")} a{" "}
                {new Date(endDate).toLocaleDateString("pt-BR")}
              </Typography>
            </>
          )}

          {/* CAIXA */}
          {reportData && reportType === "cashier" && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total de Caixas
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-cashier-total"
                  >
                    {reportData.total}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Caixas Abertos
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#10b981",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-cashier-open"
                  >
                    {reportData.open}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Caixas Fechados
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#0ea5e9",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.8rem",
                        md: "2.2rem",
                      },
                    }}
                    data-testid="report-cashier-closed"
                  >
                    {reportData.closed}
                  </Typography>
                </Paper>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Período:{" "}
                {new Date(startDate).toLocaleDateString("pt-BR")} a{" "}
                {new Date(endDate).toLocaleDateString("pt-BR")}
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
